import { NavLink, useLocation } from 'react-router';
import { ROUTES } from '@/types/routes';

/** Routes where BottomNav is hidden (focused flows with their own navigation). */
const HIDDEN_ROUTES: readonly string[] = [ROUTES.SEND, ROUTES.RECEIVE];

/**
 * Bottom navigation bar.
 * Provides primary navigation between main app sections.
 * Hidden on Send and Receive screens (focused flows with back/cancel).
 * Minimum 44x44px touch targets per WCAG/CLAUDE.md accessibility rules.
 * Tier 1 (beginner) -- always visible on non-focused pages.
 */
export function BottomNav(): React.ReactElement | null {
  const { pathname } = useLocation();

  if (HIDDEN_ROUTES.includes(pathname)) {
    return null;
  }

  return (
    <nav
      className="flex items-center justify-around bg-background"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label="Main navigation"
    >
      <NavLink
        to={ROUTES.DASHBOARD}
        end
        className={({ isActive }) =>
          `flex min-h-11 min-w-11 flex-col items-center justify-center px-4 py-2 text-sm ${
            isActive ? 'font-semibold text-primary' : 'text-muted-foreground'
          }`
        }
        aria-label="Home"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
          <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </svg>
        <span>Home</span>
      </NavLink>
      <NavLink
        to={ROUTES.HISTORY}
        className={({ isActive }) =>
          `flex min-h-11 min-w-11 flex-col items-center justify-center px-4 py-2 text-sm ${
            isActive ? 'font-semibold text-primary' : 'text-muted-foreground'
          }`
        }
        aria-label="Transaction history"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 3v5h5" />
          <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
          <path d="M12 7v5l4 2" />
        </svg>
        <span>Activity</span>
      </NavLink>
      <NavLink
        to={ROUTES.CONTACTS}
        className={({ isActive }) =>
          `flex min-h-11 min-w-11 flex-col items-center justify-center px-4 py-2 text-sm ${
            isActive ? 'font-semibold text-primary' : 'text-muted-foreground'
          }`
        }
        aria-label="Contacts"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span>Contacts</span>
      </NavLink>
      <NavLink
        to={ROUTES.SETTINGS}
        className={({ isActive }) =>
          `flex min-h-11 min-w-11 flex-col items-center justify-center px-4 py-2 text-sm ${
            isActive ? 'font-semibold text-primary' : 'text-muted-foreground'
          }`
        }
        aria-label="Settings"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        <span>Settings</span>
      </NavLink>
    </nav>
  );
}
